'use client';

import React, { useRef, useEffect, forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { PiMagnifyingGlass } from 'react-icons/pi';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

type SearchInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  iconSize?: number;
  testId?: string;
  autoBlurOnMobile?: boolean;
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      className,
      inputClassName,
      iconSize = 20,
      testId,
      autoBlurOnMobile = true,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const defaultPlaceholder = t('mainPage.samplesGrid.searchPlaceholder');
    const internalRef = useRef<HTMLInputElement | null>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    useEffect(() => {
      if (!autoBlurOnMobile) return;

      const isMobileDevice =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobile = isMobileDevice || isTouchDevice;

      if (isMobile && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.blur();
        }, 0);
      }
    }, [autoBlurOnMobile]);

    return (
      <div className={cn('relative flex-1 bg-white rounded-md', className)}>
        <div className='absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500'>
          <PiMagnifyingGlass size={iconSize} />
        </div>
        <Input
          ref={inputRef}
          className={cn('h-11 pl-10 bg-white text-base', inputClassName)}
          value={value}
          onChange={onChange}
          placeholder={placeholder || defaultPlaceholder}
          data-testid={testId}
        />
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
